import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SaplingLogo } from '@/components/icons/SaplingLogo';
import { LoginForm } from './LoginForm';
import { SignupForm } from './SignupForm';
import { ResetPasswordForm } from './ResetPasswordForm';

type AuthView = 'login' | 'signup' | 'reset-password';

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultView?: 'login' | 'signup';
  onSuccess?: () => void;
}

export function AuthModal({
  open,
  onOpenChange,
  defaultView = 'signup',
  onSuccess,
}: AuthModalProps) {
  const [view, setView] = useState<AuthView>(defaultView);

  // Sync view with defaultView when modal opens
  useEffect(() => {
    if (open) {
      setView(defaultView);
    }
  }, [open, defaultView]);

  const handleSuccess = () => {
    onSuccess?.();
    onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset to default view when closing
      setTimeout(() => setView(defaultView), 200);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="items-center pb-2">
          <SaplingLogo size="md" showText={false} className="mb-2" />
          <DialogTitle className="text-center">
            {view === 'login' && 'Welcome back'}
            {view === 'signup' && 'Create your account'}
            {view === 'reset-password' && 'Reset password'}
          </DialogTitle>
        </DialogHeader>

        {view === 'reset-password' ? (
          <ResetPasswordForm
            onSuccess={() => setView('login')}
            onBack={() => setView('login')}
          />
        ) : (
          <Tabs
            value={view}
            onValueChange={(v) => setView(v as AuthView)}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Log In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-4">
              <LoginForm
                onSuccess={handleSuccess}
                onForgotPassword={() => setView('reset-password')}
                onSwitchToSignup={() => setView('signup')}
              />
            </TabsContent>

            <TabsContent value="signup" className="mt-4">
              <SignupForm
                onSuccess={handleSuccess}
                onSwitchToLogin={() => setView('login')}
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
